import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import DeleteIcon from '@material-ui/icons/Delete';
import {Box, Button, makeStyles} from '@material-ui/core';

import { OutlinedTextField } from '../common/fields';

const useStyles = makeStyles((theme) => ({
  list: {
    '& .MuiListItem-gutters': {
      paddingLeft: 0
    }
  },
  item: {
    cursor: 'move'
  },
  iconRoot: {
    minWidth: '35px'
  },
  onHover: {
    cursor: 'move',
    backgroundColor: theme.palette.primary['50']
  },
}));

const BulletList = props => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Get props, set defaults
  const {
    data = [],
    userAllowedToEdit = true,
    onEdit,
    tooltip,
    addButtonText = t('itemFamily:addBulletButton'),
    addTitleText = t('itemFamily:addBullet'),
    maxLength = 200,
    buttonColor = 'primary',
    buttonVariant = 'contained'
  } = props;

  const [ newBullet, setNewBullet] = useState('');
  const [ onHover, setOnHover ] = useState(null);
  const [ dragged, setDragged ] = useState(null);

  const addBullet = () => {
    onEdit([...data, newBullet]);
    setNewBullet('')
  }

  const deleteBullet = (index) => {
    onEdit(data.filter((b, i) => i !== index));
  }

  const handleDragEnter = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    if (onHover === i) setOnHover(false);
  };

  const handleDragOver = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    setOnHover(i);
  };

  const handleDrop = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragged !== i) {
      const newData = [...data];
      const source = newData.splice(dragged, 1)[0];
      newData.splice(i, 0, source);
      onEdit(newData)
    }
    setOnHover(false);
    setDragged(null);
  };

  const handleDragStart = (e, i) => {
    setDragged(i)
  }

  return (
    <Box>
      <List dense={true} className={classes.list}>
        {data.map((bulletItem, i) => (
          <ListItem
            className={onHover === i ? classes.onHover: classes.item}
            key={bulletItem}
            id={bulletItem}
            draggable="true"
            onDragStart={e => handleDragStart(e, i)}
            onDrop={e => handleDrop(e, i)}
            onDragOver={e => handleDragOver(e, i)}
            onDragEnter={e => handleDragEnter(e, i)}
            onDragLeave={e => handleDragLeave(e, i)}>
            <ListItemIcon className={classes.iconRoot}>
              <DragIndicatorIcon className={classes.bullet}/>
            </ListItemIcon>
            <ListItemText primary={bulletItem} />
            <ListItemSecondaryAction>
              <IconButton title={t('common:remove')} edge="end" aria-label={t('common:remove')} onClick={() =>deleteBullet(i)}>
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <Box mt={3}>
        <OutlinedTextField
          label={addTitleText}
          name="metaTitle"
          value={newBullet}
          onChange={(e) => setNewBullet(e.target.value)}
          disabled={!userAllowedToEdit}
          maxLength={maxLength}
          tooltip={tooltip}
        />
      </Box>
      <Box mb={3}>
        <Button
          variant={buttonVariant}
          color={buttonColor}
          onClick={addBullet}
          disabled={!userAllowedToEdit}>
          {addButtonText}
        </Button>
      </Box>
    </Box>
  );
};

BulletList.propTypes = {
  data: PropTypes.array,
  userAllowedToEdit: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  tooltip: PropTypes.any,
  maxLength: PropTypes.number,
  addTitleText: PropTypes.string,
  addButtonText: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonVariant: PropTypes.string
};

export default BulletList;
